import { CHROME_ATTR, computeAnchorForPoint, isWidgetChrome } from "./anchor";
import { captureElementContext, describe } from "./element-context";
import type { ElementContext, FindingAnchor } from "../shared/schemas";

/**
 * Element pick mode.
 *
 * The overlay and label live in the MAIN document, not in the widget's shadow
 * root: a shadow root clips at `overflow: hidden` boundaries, so tracking
 * overlays rendered inside it disappear behind scroll containers.
 *
 * No global keyboard shortcut. The widget is injected into someone else's page
 * and must not take a chord the site (or another dev tool) may already own —
 * pick mode is toggled from the panel button only.
 *
 * Ported from qa-deck's `src/widget/pick.ts`; the only change is that the
 * picked element is now passed to `computeAnchorForPoint`, which needs it to
 * build the CSS-selector fallback.
 */

export interface PickResult {
  anchor: FindingAnchor;
  elementContext: ElementContext;
  /**
   * The element itself — in memory only, never sent. Multi-pick needs it to
   * number what is already picked and to un-pick by clicking it again.
   */
  element?: Element;
}

/**
 * "Pick multiple": pick mode stays on after a click, so several elements are
 * picked in ONE sequence instead of one trip to the panel each. The panel owns
 * the list (`picked`); this file only reads it to draw the numbered markers and
 * to tell a second click on a picked element (un-pick) from a new one.
 */
export interface MultiPick {
  max: number;
  picked: () => PickResult[];
  onUnpick: (index: number) => void;
}

const ACCENT = "rgba(124, 92, 255,";

let picking = false;
let curEl: Element | null = null;
let overlay: HTMLDivElement | null = null;
let label: HTMLDivElement | null = null;
let lastPoint = { x: 0, y: 0 };
let onResult: ((result: PickResult) => void) | null = null;
let onStateChange: ((active: boolean) => void) | null = null;
let multi: MultiPick | null = null;
/** One numbered outline per picked element, while multi-pick is on. */
let markers: HTMLDivElement[] = [];

function ensureOverlay(): void {
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.setAttribute(CHROME_ATTR, "");
  Object.assign(overlay.style, {
    position: "fixed",
    zIndex: "2147483646",
    pointerEvents: "none",
    background: `${ACCENT} 0.15)`,
    outline: `2px solid ${ACCENT} 0.9)`,
    borderRadius: "2px",
    display: "none",
  });
  label = document.createElement("div");
  label.setAttribute(CHROME_ATTR, "");
  Object.assign(label.style, {
    position: "fixed",
    zIndex: "2147483646",
    pointerEvents: "none",
    font: "11px ui-monospace, Menlo, monospace",
    background: "#1e1b2e",
    color: "#ddd6fe",
    padding: "2px 6px",
    borderRadius: "4px",
    maxWidth: "60vw",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "none",
  });
  document.documentElement.append(overlay, label);
}

function position(el: Element): void {
  if (!overlay || !label) return;
  const r = el.getBoundingClientRect();
  Object.assign(overlay.style, {
    display: "block",
    left: `${r.left}px`,
    top: `${r.top}px`,
    width: `${r.width}px`,
    height: `${r.height}px`,
  });
  label.textContent = multi
    ? multiHint(el, multi)
    : `${describe(el)} · click to attach · Esc to cancel`;
  Object.assign(label.style, {
    display: "block",
    left: `${Math.max(4, r.left)}px`,
    top: `${r.top > 24 ? r.top - 22 : r.bottom + 4}px`,
  });
}

function multiHint(el: Element, m: MultiPick): string {
  const picked = m.picked();
  const at = picked.findIndex((p) => p.element === el);
  if (at >= 0)
    return `${describe(el)} · #${at + 1} · click to remove · Esc when done`;
  return `${describe(el)} · click to add (${picked.length + 1} of ${m.max}) · Esc when done`;
}

/** Redraw the numbered outlines over every picked element still on the page. */
function drawMarkers(): void {
  for (const marker of markers) marker.remove();
  markers = [];
  if (!multi) return;
  multi.picked().forEach((pick, i) => {
    const el = pick.element;
    // A re-rendering page can drop a picked node; its anchor is still
    // evidence, there is just nothing left to outline.
    if (!el || !el.isConnected) return;
    const r = el.getBoundingClientRect();
    const marker = document.createElement("div");
    marker.setAttribute(CHROME_ATTR, "");
    marker.setAttribute("data-qa-deck-pick-marker", String(i + 1));
    Object.assign(marker.style, {
      position: "fixed",
      zIndex: "2147483645",
      pointerEvents: "none",
      left: `${r.left}px`,
      top: `${r.top}px`,
      width: `${r.width}px`,
      height: `${r.height}px`,
      outline: `2px solid ${ACCENT} 0.9)`,
      background: `${ACCENT} 0.08)`,
      borderRadius: "2px",
    });
    const badge = document.createElement("div");
    badge.textContent = String(i + 1);
    Object.assign(badge.style, {
      position: "absolute",
      left: "-2px",
      top: r.top > 18 ? "-18px" : "0",
      minWidth: "16px",
      height: "16px",
      padding: "0 4px",
      boxSizing: "border-box",
      font: "bold 11px/16px ui-monospace, Menlo, monospace",
      textAlign: "center",
      color: "#fff",
      background: "rgb(124, 92, 255)",
      borderRadius: "8px",
    });
    marker.append(badge);
    document.documentElement.append(marker);
    markers.push(marker);
  });
}

const onHover = (e: Event) => {
  const el = e.target;
  if (!(el instanceof Element)) return;
  if (el === overlay || el === label || isWidgetChrome(el)) return;
  curEl = el;
  position(el);
};

const onMove = (e: Event) => {
  const me = e as MouseEvent;
  lastPoint = { x: me.clientX, y: me.clientY };
};

const onScroll = () => {
  if (curEl) position(curEl);
  drawMarkers();
};

const swallow = (e: Event) => {
  e.preventDefault();
  e.stopImmediatePropagation();
};

const onClick = (e: Event) => {
  const me = e as MouseEvent;
  if (me.target instanceof Element && isWidgetChrome(me.target)) return; // panel stays usable
  swallow(e);
  lastPoint = { x: me.clientX, y: me.clientY };
  const el = curEl || (me.target instanceof Element ? me.target : null);
  if (multi) {
    if (!el) return;
    const at = multi.picked().findIndex((p) => p.element === el);
    if (at >= 0) multi.onUnpick(at);
    else onResult?.(capture(el));
    // Full ⇒ the sequence is over; there is nothing left to pick.
    if (multi && multi.picked().length >= multi.max) stopPick();
    else refreshPickMarkers();
    return;
  }
  // Hold the handler: stopPick() clears it, and the capture must survive
  // teardown — dropping it here silently produced a pick with no result.
  const handler = onResult;
  stopPick();
  if (el && handler) handler(capture(el));
};

function capture(el: Element): PickResult {
  return {
    anchor: computeAnchorForPoint(lastPoint.x, lastPoint.y, el),
    elementContext: captureElementContext(el),
    element: el,
  };
}

const onKeyDown = (e: Event) => {
  if ((e as KeyboardEvent).key === "Escape") {
    swallow(e);
    stopPick();
  }
};

const SWALLOWED = [
  "pointerdown",
  "mousedown",
  "mouseup",
  "auxclick",
  "dblclick",
];

export function startPick(
  handler: (result: PickResult) => void,
  stateChange?: (active: boolean) => void,
  multiple?: MultiPick,
): void {
  if (picking) return;
  picking = true;
  onResult = handler;
  onStateChange = stateChange ?? null;
  multi = multiple ?? null;
  ensureOverlay();
  drawMarkers();
  window.addEventListener("mouseover", onHover, true);
  window.addEventListener("mousemove", onMove, true);
  window.addEventListener("scroll", onScroll, true);
  window.addEventListener("resize", onScroll, true);
  window.addEventListener("click", onClick, true);
  window.addEventListener("keydown", onKeyDown, true);
  for (const t of SWALLOWED) window.addEventListener(t, swallow, true);
  onStateChange?.(true);
}

export function stopPick(): void {
  if (!picking) return;
  picking = false;
  curEl = null;
  if (overlay) overlay.style.display = "none";
  if (label) label.style.display = "none";
  window.removeEventListener("mouseover", onHover, true);
  window.removeEventListener("mousemove", onMove, true);
  window.removeEventListener("scroll", onScroll, true);
  window.removeEventListener("resize", onScroll, true);
  window.removeEventListener("click", onClick, true);
  window.removeEventListener("keydown", onKeyDown, true);
  for (const t of SWALLOWED) window.removeEventListener(t, swallow, true);
  multi = null;
  drawMarkers();
  onStateChange?.(false);
  onStateChange = null;
  onResult = null;
}

export function isPicking(): boolean {
  return picking;
}

/** Is the running pick a "Pick multiple" sequence? */
export function isPickingMultiple(): boolean {
  return picking && multi !== null;
}

/** Redraw the numbers after the panel changed the list (a chip's ✕). */
export function refreshPickMarkers(): void {
  drawMarkers();
  if (curEl && label && multi) label.textContent = multiHint(curEl, multi);
}
